import React, {useState, useEffect, useCallback} from 'react';
import {auth, db, storage, getAlbumList} from '../../firebase';
import {signInWithEmailAndPassword, onAuthStateChanged, signOut} from "firebase/auth";
import {collection, doc, setDoc, serverTimestamp, getDocs} from "firebase/firestore";
import {ref, uploadString} from "firebase/storage";
import './admin.css';
import {albumArt} from '../game/functions/album-art-modified.js';
import {Helmet} from "react-helmet-async";

// --- Spotify API Configuration ---
const SPOTIFY_CLIENT_ID = '3f974573800a4ff5b325de9795b8e603';
const SPOTIFY_CLIENT_SECRET = 'ff188d2860ff44baa57acc79c121a3b9';

const SPOTIFY_TOKEN_ENDPOINT = 'https://accounts.spotify.com/api/token';
const SPOTIFY_SEARCH_ENDPOINT = 'https://api.spotify.com/v1/search';

let spotifyAccessToken = null;
let tokenExpiryTime = 0;

// Helper function to clean album titles
const cleanAlbumTitle = (title) => {
  if (typeof title !== 'string') return title;
  return title.replace(/\s*\(([^)]*remaster[^)]*|[^)]*deluxe[^)]*)\)/gi, '').trim();
};

// Helper function to sanitize strings for use as Firestore document IDs
const sanitizeForDocId = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(/\s+/g, '_')
    .replace(/[.#$[\]*~/?]/g, '');
};

// Helper function to format a field for CSV: quotes if it contains comma or quote, and escapes existing quotes.
const formatCSVField = (fieldValue) => {
  if (typeof fieldValue !== 'string') {
    return '';
  }
  const strValue = String(fieldValue);
  if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n') || strValue.includes('\r')) {
    const escapedValue = strValue.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }
  return strValue;
};


const parseLocalAlbumEntry = (entryString) => {
  const fields = [];
  let currentField = '';
  let inQuotes = false;
  for (let i = 0; i < entryString.length; i++) {
    const char = entryString[i];
    if (char === '"') {
      if (inQuotes && i + 1 < entryString.length && entryString[i + 1] === '"') {
        currentField += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      if (fields.length === 0) {
        fields.push(currentField);
        currentField = '';
      } else {
        currentField += char;
      }
    } else {
      currentField += char;
    }
  }
  fields.push(currentField);

  if (fields.length >= 2) {
    const dequote = (str) => {
      const trimmed = str.trim();
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return trimmed.slice(1, -1).replace(/""/g, '"');
      }
      return trimmed;
    };
    const albumName = dequote(fields[0]);
    let artistName = dequote(fields.slice(1).join(','));

    if (artistName.endsWith(',')) {
      artistName = artistName.slice(0, -1).trim();
    }

    return {
      name: albumName,
      artist: artistName
    };
  }
  return null;
};


async function getSpotifyApiToken() {
  if (spotifyAccessToken && Date.now() < tokenExpiryTime) {
    return spotifyAccessToken;
  }
  const response = await fetch(SPOTIFY_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': 'Basic ' + btoa(SPOTIFY_CLIENT_ID + ':' + SPOTIFY_CLIENT_SECRET)
    },
    body: 'grant_type=client_credentials'
  });
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      errorData = {error_description: response.statusText};
    }
    console.error("Spotify token API error:", errorData);
    throw new Error(`Failed to get Spotify token: ${errorData.error_description || response.statusText}. Please verify network connection and Spotify API status.`);
  }
  const data = await response.json();
  spotifyAccessToken = data.access_token;
  tokenExpiryTime = Date.now() + (data.expires_in * 1000) - 60000;
  return spotifyAccessToken;
}

async function fetchActualSpotifyResults(query, resultsLimitToFetch) {
  if (!query.trim()) return [];
  try {
    const token = await getSpotifyApiToken();
    const apiLimit = Math.min(resultsLimitToFetch, 50);
    const searchParams = new URLSearchParams({q: query, type: 'album', limit: apiLimit.toString()});
    const response = await fetch(`${SPOTIFY_SEARCH_ENDPOINT}?${searchParams}`, {headers: {'Authorization': `Bearer ${token}`}});

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {error: {message: response.statusText}};
      }
      console.error("Spotify search API error:", errorData);
      throw new Error(`Spotify search failed: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    const results = [];
    if (data.albums && data.albums.items) {
      data.albums.items.forEach(album => {
        if (album.album_type === 'album') {
          results.push({
            id: album.id,
            name: cleanAlbumTitle(album.name),
            artist: album.artists.map(a => a.name).join(', '),
            type: 'Album',
            coverUrl: album.images && album.images.length > 0 ? album.images[album.images.length - 1]?.url : null,
            externalUrl: album.external_urls?.spotify
          });
        }
      });
    }
    return results.slice(0, resultsLimitToFetch);
  } catch (error) {
    console.error("Error fetching actual Spotify results:", error.message);
    throw error;
  }
}

const Admin = (props) => {
  const {theme} = props;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [authError, setAuthError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [localAlbums, setLocalAlbums] = useState([]);
  const [isLoadingLocalAlbums, setIsLoadingLocalAlbums] = useState(false);
  const [albumsToAdd, setAlbumsToAdd] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [resultsLimit, setResultsLimit] = useState(10);
  const [targetDateDisplay, setTargetDateDisplay] = useState(null);
  const [isLoadingTargetDate, setIsLoadingTargetDate] = useState(false);

  const calculateTargetDate = useCallback(async () => {
    if (!db) {
      console.error("Firestore 'db' is not initialized.");
      setTargetDateDisplay("Error: DB not ready");
      return;
    }
    setIsLoadingTargetDate(true);
    try {
      const albumsCollectionRef = collection(db, "albums");
      const querySnapshot = await getDocs(albumsCollectionRef);
      const totalAlbumsInDB = querySnapshot.size;

      const today = new Date();
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + totalAlbumsInDB - 1);

      const options = {year: 'numeric', month: 'long', day: 'numeric'};
      setTargetDateDisplay(targetDate.toLocaleDateString(undefined, options));

    } catch (error) {
      console.error("Error calculating target date:", error);
      setTargetDateDisplay("Error calculating date");
    } finally {
      setIsLoadingTargetDate(false);
    }
  }, []);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setUserId(currentUser.uid);
        if (localAlbums.length === 0) {
          setIsLoadingLocalAlbums(true);
          try {
            if (typeof getAlbumList === 'function') {
              getAlbumList(setLocalAlbums);
            } else {
              console.error("getAlbumList function was not imported correctly or is not defined in firebase.js. Cannot fetch local albums.");
              setIsLoadingLocalAlbums(false);
            }
          } catch (error) {
            console.error("Error initiating getAlbumList:", error);
            setIsLoadingLocalAlbums(false);
          }
        }
        calculateTargetDate();
      } else {
        setUserId(null);
        setSearchQuery('');
        setSearchResults([]);
        setSearchError('');
        setLocalAlbums([]);
        setIsLoadingLocalAlbums(false);
        setAlbumsToAdd([]);
        setSaveMessage('');
        setTargetDateDisplay(null); // Clear target date on logout
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAlbums.length, calculateTargetDate]);

  useEffect(() => {
    if (localAlbums.length > 0 && isLoadingLocalAlbums) {
      setIsLoadingLocalAlbums(false);
    } else if (localAlbums.length === 0 && isLoadingLocalAlbums) {
      setIsLoadingLocalAlbums(false);
    }
  }, [localAlbums, isLoadingLocalAlbums]);


  const performSearch = useCallback(async (currentQuery, limit) => {
    if (!currentQuery.trim()) {
      setSearchResults([]);
      setSearchError('');
      return;
    }
    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);
    try {
      const rawResults = await fetchActualSpotifyResults(currentQuery, limit);
      let resultsWithArt = rawResults;
      if (rawResults.some(item => !item.coverUrl)) {
        resultsWithArt = await Promise.all(
          rawResults.map(async (item) => {
            if (item.coverUrl) return item;
            let imageUrl = null;
            if (item.artist) {
              try {
                imageUrl = await albumArt(item.artist.split(',')[0].trim(), {album: item.name, size: 'small'});
              } catch (e) { /* console.warn(`albumArt (album) failed for ${item.name}: ${e.message}`); */
              }
            }
            return {...item, coverUrl: imageUrl || item.coverUrl};
          })
        );
      }
      setSearchResults(resultsWithArt);
    } catch (error) {
      console.error("Error during search process in UI:", error.message);
      setSearchError(`Search failed: ${error.message}. Please check console for details.`);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const handler = setTimeout(() => {
        performSearch(searchQuery, resultsLimit);
      }, 600);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, resultsLimit, performSearch]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
    } catch (err) {
      setAuthError(err.message);
      console.error("Firebase login error: ", err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Firebase logout error: ", err);
      setAuthError("Failed to log out.");
    }
  };

  const isAlbumInLocalDB = useCallback((spotifyAlbumName, spotifyArtistName) => {
    if (!spotifyAlbumName || !spotifyArtistName || !Array.isArray(localAlbums) || localAlbums.length === 0) {
      return false;
    }

    const normalizeStr = (str) => {
      if (typeof str !== 'string') return '';
      return str.toLowerCase().replace(/\s+/g, ' ').trim();
    }

    const sAlbumCleaned = cleanAlbumTitle(spotifyAlbumName);
    const sAlbumNormalized = normalizeStr(sAlbumCleaned);
    const sArtistNormalized = normalizeStr(spotifyArtistName);

    return localAlbums.some(localAlbumString => {
      if (typeof localAlbumString !== 'string') return false;

      const parsedLocal = parseLocalAlbumEntry(localAlbumString);
      if (!parsedLocal || !parsedLocal.name || !parsedLocal.artist) return false;

      const finalLocalAlbumNameCleaned = cleanAlbumTitle(parsedLocal.name);
      const finalLocalArtistName = parsedLocal.artist;

      const localAlbumNormalized = normalizeStr(finalLocalAlbumNameCleaned);
      const localArtistNormalized = normalizeStr(finalLocalArtistName);

      const albumMatch = localAlbumNormalized === sAlbumNormalized;
      const artistMatch = sArtistNormalized.includes(localArtistNormalized);

      return albumMatch && artistMatch;
    });
  }, [localAlbums]);

  const handleSelectAlbum = (album) => {
    setAlbumsToAdd(prev => {
      const isAlreadySelected = prev.find(a => a.id === album.id);
      if (isAlreadySelected) {
        return prev.filter(a => a.id !== album.id);
      } else {
        if (!isAlbumInLocalDB(album.name, album.artist)) {
          return [...prev, {id: album.id, name: album.name, artist: album.artist}];
        }
        return prev;
      }
    });
  };

  const isAlbumSelected = (albumId) => {
    return albumsToAdd.some(a => a.id === albumId);
  };

  const convertToCSV = (arrayOfAlbumStrings) => {
    const csvRows = arrayOfAlbumStrings.join("\n");
    return csvRows;
  };

  const handleSaveAlbums = async () => {
    if (albumsToAdd.length === 0 || !userId) {
      setSaveMessage("No new albums selected to save, or user not identified.");
      if (albumsToAdd.length === 0 && isSaving === false) {
        setTimeout(() => setSaveMessage(''), 3000);
      }
      return;
    }

    const albumsToProcessForSaving = albumsToAdd.filter(album => {
      const isPlaceholder = album.name.toLowerCase() === 'album' && album.artist.toLowerCase() === 'artist';
      if (isPlaceholder) {
      }
      return !isPlaceholder;
    });

    if (albumsToProcessForSaving.length === 0) {
      setSaveMessage("Selected items were placeholders and will not be saved.");
      setIsSaving(false);
      setAlbumsToAdd([]);
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);
    setSaveMessage('Saving albums...');

    const existingAlbumObjects = localAlbums.map(localAlbumString => {
      const parsed = parseLocalAlbumEntry(localAlbumString);
      if (parsed && parsed.name && parsed.artist) {
        return {name: cleanAlbumTitle(parsed.name), artist: parsed.artist};
      }
      return null;
    }).filter(Boolean);

    const normalizeStrForMapKey = (str) => (typeof str === 'string' ? str.toLowerCase().replace(/\s+/g, ' ').trim() : '');
    const allAlbumsMap = new Map();

    existingAlbumObjects.forEach(album => {
      const key = `${normalizeStrForMapKey(album.name)}-${normalizeStrForMapKey(album.artist)}`;
      if (!allAlbumsMap.has(key)) {
        allAlbumsMap.set(key, album);
      }
    });

    albumsToProcessForSaving.forEach(album => {
      const key = `${normalizeStrForMapKey(album.name)}-${normalizeStrForMapKey(album.artist)}`;
      allAlbumsMap.set(key, {name: album.name, artist: album.artist});
    });

    const uniqueCombinedAlbumObjects = Array.from(allAlbumsMap.values());

    const finalAlbumStringsForStateAndCSV = uniqueCombinedAlbumObjects.map(album =>
      `${formatCSVField(album.name)}, ${formatCSVField(album.artist)}`
    );

    setLocalAlbums(finalAlbumStringsForStateAndCSV);

    try {
      const csvData = convertToCSV(finalAlbumStringsForStateAndCSV);
      const storagePath = `albums/albums.csv`;
      const csvFileRef = ref(storage, storagePath);
      await uploadString(csvFileRef, csvData, 'raw', {contentType: 'text/csv'});

      const firestoreCollectionPath = "albums";

      for (const album of albumsToProcessForSaving) {
        let customDocId = 'N/A_ID';
        try {
          const sanitizedAlbumName = sanitizeForDocId(album.name);
          const sanitizedArtistName = sanitizeForDocId(album.artist);

          if (!sanitizedAlbumName || !sanitizedArtistName) {
            continue;
          }
          customDocId = `${sanitizedAlbumName}-${sanitizedArtistName}`;

          const albumDocRef = doc(db, firestoreCollectionPath, customDocId);

          await setDoc(albumDocRef, {
            album: album.name,
            artist: album.artist,
            today: false,
            addedAt: serverTimestamp(),
            userId: userId
          });
        } catch (firestoreError) {
          console.error(`Error adding album "${album.name}" to Firestore with ID "${customDocId}":`, firestoreError);
        }
      }

      setAlbumsToAdd([]);
      setSaveMessage('Albums saved successfully!');
      calculateTargetDate(); // Recalculate target date after saving new albums
    } catch (error) {
      console.error("Error saving albums:", error);
      setSaveMessage(`Error saving albums: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };


  const handleSearchQueryChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleResultsLimitChange = (e) => {
    setResultsLimit(parseInt(e.target.value, 10));
  };


  if (!user) {
    return (
      <div className="admin-container">
        <h2>Admin Login</h2>
        <form onSubmit={handleLogin} className="admin-form">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required
                 className="admin-input"/>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                 required className="admin-input"/>
          <button type="submit" className="admin-button">Login</button>
          {authError && <p className="admin-error">{authError}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className={`admin-container ${theme}Text`}>
      <Helmet>
        <meta name="robots" content="noindex"/>
      </Helmet>

      {isLoadingLocalAlbums &&
        <p style={{fontStyle: 'italic', fontSize: '0.9em', textAlign: 'center', marginBottom: '10px'}}>Loading your
          local album list...</p>}

      {/* Target Date Display */}
      <div className="target-date-info"
           style={{margin: '10px 0', fontSize: '0.95em', textAlign: 'center', marginBottom: '20px'}}>
        {isLoadingTargetDate ? (
          <p>Calculating target date...</p>
        ) : targetDateDisplay ? (
          <p>Target Completion Date: {targetDateDisplay}</p>
        ) : (
          <p>Could not determine target date.</p>
        )}
      </div>

      <div className="album-search-section">
        <h3>Search Spotify for Albums</h3>
        <div className="album-search-controls"
             style={{display: 'flex', alignItems: 'center', marginBottom: '15px', gap: '10px'}}>
          <input
            type="search"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            placeholder="Type an album name..."
            className="album-search-input"
            style={{flexGrow: 1}}
          />
          <div className="results-limit-selector">
            <label htmlFor="resultsLimit" style={{marginRight: '5px', fontSize: '0.9em'}}>Results:</label>
            <select
              id="resultsLimit"
              value={resultsLimit}
              onChange={handleResultsLimitChange}
              className="admin-input"
              style={{padding: '8px', fontSize: '0.9em'}}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>


        {searchError && <p className="search-error-text">{searchError}</p>}
        {isSearching && !searchError && <p className="loading-text">Searching Spotify for Albums...</p>}

        {!isSearching && searchResults.length > 0 && (
          <ul className="album-results-list" style={{color: 'black'}}>
            {searchResults.map((item) => {
              const isInLocalDb = isAlbumInLocalDB(item.name, item.artist);
              const isSelectedForAdding = isAlbumSelected(item.id);
              const canBeAdded = !isInLocalDb;

              return (
                <li
                  key={item.id}
                  className={`album-item ${isSelectedForAdding ? 'album-item-selected' : ''} ${isInLocalDb ? 'album-item-in-db' : ''}`}
                  onClick={() => {
                    if (canBeAdded || isSelectedForAdding) {
                      handleSelectAlbum(item);
                    }
                  }}
                  style={{cursor: (canBeAdded || isSelectedForAdding) ? 'pointer' : 'default'}}
                  title={isInLocalDb && !isSelectedForAdding ? "Already in your local list" : (isSelectedForAdding ? "Click to unselect" : "Click to add")}
                >
                  <img src={item.coverUrl || 'https://via.placeholder.com/60?text=No+Art'}
                       alt={`Cover for ${item.name}`}/>
                  <div className="album-item-details">
                    <a href={item.externalUrl || '#'} target="_blank" rel="noopener noreferrer"
                       style={{textDecoration: 'none', color: 'inherit'}}>
                      <span className="album-item-name">{item.name}</span>
                    </a>
                    {item.artist && <span className="album-item-artist">By: {item.artist}</span>}
                  </div>
                  {isInLocalDb && !isSelectedForAdding && <span title="Already in your local list" style={{
                    color: 'green',
                    marginLeft: '10px',
                    fontSize: '1.2em'
                  }}>✔️</span>}
                  {isSelectedForAdding && <span title="Selected to add"
                                                style={{color: 'blue', marginLeft: '10px', fontSize: '1.2em'}}>➕</span>}
                </li>
              );
            })}
          </ul>
        )}
        {!isSearching && searchQuery.trim() !== "" && searchResults.length === 0 && !searchError && (
          <p className="no-results-text">No albums found on Spotify for your query.</p>
        )}
      </div>

      {albumsToAdd.length > 0 && (
        <div className="save-section" style={{marginTop: '20px', textAlign: 'center'}}>
          <h4>Albums to Add:</h4>
          <ul style={{listStyle: 'none', padding: 0}}>
            {albumsToAdd.map(album => <li key={album.id}>{album.name} - {album.artist}</li>)}
          </ul>
          <button onClick={handleSaveAlbums} disabled={isSaving} className="admin-button"
                  style={{backgroundColor: '#28a745'}}>
            {isSaving ? 'Saving...' : `Save ${albumsToAdd.length} Album(s)`}
          </button>
        </div>
      )}
      {saveMessage &&
        <p style={{marginTop: '10px', color: saveMessage.startsWith('Error') ? 'red' : 'green'}}>{saveMessage}</p>}


      <button onClick={handleLogout} className="admin-button admin-logout-button" style={{marginTop: '30px'}}>Logout
      </button>
    </div>
  );
};

export default Admin;
